/* Manages color theme (neutral | warm).
   Applies theme class on <html> and persists to localStorage. */

import { useState, useLayoutEffect, useCallback } from "react";

const THEME_KEY = "feedkiwi-theme";

export type ColorThemeName = "neutral" | "warm" | "kiwi";

const THEME_CLASSES: Record<ColorThemeName, string | null> = {
  neutral: null,
  warm: "theme-warm",
  kiwi: "theme-kiwi",
};

export const COLOR_THEME_OPTIONS: {
  value: ColorThemeName;
  label: string;
}[] = [
  { value: "neutral", label: "Neutral" },
  { value: "warm", label: "Warm" },
  { value: "kiwi", label: "Kiwi" },
];

function readInitialTheme(): ColorThemeName {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === "warm") return "warm";
  if (stored === "kiwi") return "kiwi";
  return "neutral";
}

export function useColorTheme() {
  const [colorTheme, setColorTheme] =
    useState<ColorThemeName>(readInitialTheme);

  useLayoutEffect(() => {
    const root = document.documentElement;

    for (const cls of Object.values(THEME_CLASSES)) {
      if (cls) root.classList.remove(cls);
    }
    const themeClass = THEME_CLASSES[colorTheme];
    if (themeClass) root.classList.add(themeClass);
    localStorage.setItem(THEME_KEY, colorTheme);
  }, [colorTheme]);

  const cycleColorTheme = useCallback(
    () =>
      setColorTheme((prev) => {
        const idx = COLOR_THEME_OPTIONS.findIndex((o) => o.value === prev);
        return COLOR_THEME_OPTIONS[(idx + 1) % COLOR_THEME_OPTIONS.length]
          .value;
      }),
    [],
  );

  return { colorTheme, setColorTheme, cycleColorTheme };
}
